import { isFiniteNumber } from './number';

/**
 * Randomizes the items of an array by reference.
 * @param sourceArray
 */
export function shuffle(sourceArray: Array<any>) {
  for (let i = 0; i < sourceArray.length - 1; i++) {
    const j = i + Math.floor(Math.random() * (sourceArray.length - i));

    const temp = sourceArray[j];
    sourceArray[j] = sourceArray[i];
    sourceArray[i] = temp;
  }

  return sourceArray;
}

/**
 * Generates an array of ascending numbers from 1 to n.
 *
 * @param {number} n - The number up to which the array should be filled.
 * @return {number[]} - An array filled with numbers from 1 to n in ascending order.
 *
 * @example
 * generateAscendingArray(3) -> Returns [1, 2, 3]
 *
 */
export const generateAscendingArray = (n: number): number[] => {
  const array: number[] = [];

  if (!n) return array;
  if (!isFiniteNumber(n)) return array;
  if (n < 1) return array;

  for (let i = 1; i <= n; i++) {
    array.push(i);
  }
  return array;
};

/**
 * Moves an item within an array to a new index.
 *
 * @param array
 * @param fromIndex
 * @param toIndex
 * @returns New array with the item moved to the new index.
 */
export const moveItemInArray = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
  const newArray = [...array];
  if (!newArray[fromIndex] || !newArray[toIndex]) return newArray;

  const item = newArray.splice(fromIndex, 1)[0];
  newArray.splice(toIndex, 0, item);
  return newArray;
};

/**
 * Random pick an item from an array.
 */
export const randomPick = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

/**
 * Composes a series of functions into a single function that applies them in sequence.
 *
 * @template T - The type of the argument and return value of the functions.
 * @param {...Array<(arg: T) => T>} fns - The functions to be composed.
 * @returns {(x: T) => T} - The composed function.
 */
export const pipe =
  <T>(...fns: Array<(arg: T) => T>) =>
  (x: T): T =>
    fns.reduce((acc, fn) => fn(acc), x);

/**
 * Checks if two arrays are equal or not
 *
 * @param a {T[]} - The first array to compare.
 * @param b {T[]} - The second array to compare.
 * @returns {boolean} - True if the arrays are equal, false otherwise.
 */
export const arraysEqual = <T>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

/**
 * Remove an item from an array by index value
 *
 * @param arr {T[]} - The array to remove the item from.
 * @param index {number} - The index of the item to remove.
 * @returns {T[]} - The new array with the item removed.
 */
export const removeAtIndex = <T>(arr: T[], index: number) => {
  if (index < 0 || index >= arr.length || !Number.isInteger(index)) return arr;

  return [...arr.slice(0, index), ...arr.slice(index + 1)];
};
/**
 * Toggles the presence of a value in an array. If the value is present, it will be removed.
 * If the value is not present, it will be added.
 *  TODO: Can replace `toggleAccordionExpand` in ImageWidget and MultipleChoices during maintenance phase.
 * @param array - The array to toggle the value in.
 * @param value - The value to toggle.
 * @returns A new array with the value toggled.
 */
export const toggleItemInArray = (array: number[], value: number): number[] => {
  const index = array.indexOf(value);
  if (index > -1) {
    const copy = [...array];
    copy.splice(index, 1);
    return copy;
  } else {
    return [...array, value];
  }
};
